import { useCallback, useEffect, useState } from 'react';
import { useTheme, useTokens } from '../../shared/theme';
import {
  Card, SectionTitle, Button, ErrorState, ScrollTable,
} from '../../shared/uiKit';
import { TableSkeleton } from '../../shared/Skeleton.jsx';
import { getAuditLog } from '../../shared/services/institutions';

/**
 * The school's own audit trail (institution-scoped, newest first) plus a
 * refresh action. The RN InstitutionAdmin surface had no audit view.
 *
 * Two surfaces mount this: the portal, where a school admin reads plain event
 * names, short details and actor labels; and the console institution drill-in,
 * which keeps the raw enums, IDs, emails and mono rows.
 */
export default function PortalAuditView({ institutionId }) {
  const portal = useTheme() === 'portal';
  const [entries, setEntries] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getAuditLog(institutionId);
      setEntries(data?.entries || []);
    } catch (err) {
      setEntries(null);
    } finally {
      setLoading(false);
    }
  }, [institutionId]);

  useEffect(() => { load(); }, [load]);

  let body = null;
  if (!loading && entries && entries.length > 0) {
    body = portal ? <PortalAuditList entries={entries} /> : <AuditTable entries={entries} />;
  }

  return (
    <Card>
      {/* The portal page h1 already reads "Audit log". */}
      <SectionTitle
        hideTitle={portal}
        right={(
          <Button
            small
            variant="secondary"
            label={loading ? 'Refreshing...' : 'Refresh'}
            onClick={load}
            disabled={loading}
          />
        )}
      >
        Audit log
      </SectionTitle>
      {loading ? (
        <TableSkeleton rows={5} columns={4} className="py-2" />
      ) : null}

      {!loading && entries && entries.length === 0 ? (
        <MutedRow>No audit entries yet.</MutedRow>
      ) : null}

      {!loading && entries === null ? (
        <ErrorState message="Couldn't load the audit log." onRetry={load} />
      ) : null}

      {body}
    </Card>
  );
}

// ---- portal: plain language -------------------------------------------------

const ACTION_LABEL = {
  SPONSORSHIP_OPT_IN: 'Student joined sponsored seats',
  SPONSORSHIP_OPT_OUT: 'Student left sponsored seats',
  SEATS_ADJUSTED: 'Seat count changed',
  INSTITUTION_BRANDING_SET: 'Branding updated',
  BRANDING_LOGO_UPDATED: 'Logo updated',
  ADMIN_INVITED: 'Admin invited',
  INSTITUTION_SLUG_RENAMED: 'Portal address changed',
  INSTITUTION_CREATED: 'Portal created',
};

// Field paths a branding change records, in the Branding page's words. A path
// with no label (accentInClayBand rides along with accentColor) is dropped.
const FIELD_LABEL = {
  'branding.accentColor': 'Accent color',
  'branding.displayName': 'Display name',
  'branding.logoSource': 'Logo choice',
  'branding.logoFileId': 'Logo',
};

// Permission names as the Admins page shows them.
const SCOPE_LABEL = {
  ANALYTICS_VIEW: 'Analytics',
  SEATS_MANAGE: 'Seats',
  BRANDING_MANAGE: 'Branding',
  ADMINS_MANAGE: 'Admins',
  BILLING_VIEW: 'Billing',
};

// Array metadata that has its own wording: label and per-item mapping.
const LIST_FORMAT = {
  fields: ['Changed', (item) => { return FIELD_LABEL[item] || null; }],
  scopes: ['Permissions', (item) => { return SCOPE_LABEL[item] || plainValue(item); }],
};

const OBJECT_ID = /^[0-9a-f]{24}$/i;
const ENUM_VALUE = /^[A-Z][A-Z0-9_]*$/;

// SEATS_ADJUSTED -> "Seats adjusted"; seatsPurchased -> "Seats purchased".
const sentenceCase = (value) => {
  const words = String(value)
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/_/g, ' ')
    .trim()
    .toLowerCase();
  return words.charAt(0).toUpperCase() + words.slice(1);
};

const plainValue = (value) => {
  if (typeof value === 'boolean') { return value ? 'on' : 'off'; }
  const text = String(value);
  return ENUM_VALUE.test(text) ? sentenceCase(text) : text;
};

export const portalEventLabel = (entry) => {
  return ACTION_LABEL[entry.action] || sentenceCase(entry.action || 'Event');
};

/** Short phrases for a school admin: no IDs, no key=value pairs, blank when empty. */
export const portalDetails = (entry) => {
  const meta = entry.metadata || {};
  const target = entry.target || {};
  if (entry.action === 'SEATS_ADJUSTED') {
    const parts = [];
    if (typeof meta.seatsPurchased === 'number') {
      parts.push(`to ${meta.seatsPurchased.toLocaleString()} seat${meta.seatsPurchased === 1 ? '' : 's'}`);
    }
    if (meta.seatPlan) { parts.push(`${plainValue(meta.seatPlan)} plan`); }
    return parts.join(' · ');
  }
  if (entry.action === 'INSTITUTION_SLUG_RENAMED' && meta.from && meta.to) {
    return `${meta.from} to ${meta.to}`;
  }
  const parts = [];
  if (target.email) { parts.push(target.email); }
  Object.keys(meta).forEach((key) => {
    if (/(^id|Id|_id|ID)$/.test(key)) { return; }
    const value = meta[key];
    if (value === null || value === undefined || value === '') { return; }
    if (Array.isArray(value)) {
      const [label, format] = LIST_FORMAT[key] || [sentenceCase(key), plainValue];
      const items = value
        .filter((item) => { return ['string', 'number'].includes(typeof item) && !OBJECT_ID.test(String(item)); })
        .map(format)
        .filter(Boolean);
      if (items.length) { parts.push(`${label}: ${items.join(', ')}`); }
      return;
    }
    if (typeof value === 'object' || OBJECT_ID.test(String(value))) { return; }
    parts.push(`${sentenceCase(key)}: ${plainValue(value)}`);
  });
  return parts.join(' · ');
};

/**
 * Who acted, in words a school admin can use. Staff are named, never emailed.
 * The server nulls the actor on student opt-in/out and seat revokes, so those
 * fall through to "A student" (actor type USER) or "An admin".
 */
export const portalActor = (entry) => {
  const name = entry.actorName?.trim();
  if (entry.actorType === 'SUPER_ADMIN') {
    return name ? `${name}, Coffee Chats staff` : 'Coffee Chats staff';
  }
  if (entry.actorDeleted) { return 'Former admin'; }
  if (entry.actorType === 'USER') { return 'A student'; }
  const email = entry.actorEmail?.trim();
  return [name, email && email !== name ? email : null].filter(Boolean).join(' · ') || 'An admin';
};

const formatTime = (entry) => {
  return entry.createdAt ? new Date(entry.createdAt).toLocaleString() : '';
};

function PortalAuditList({ entries }) {
  const t = useTokens();
  const headClass = 'py-2 pr-3 text-[13px] uppercase tracking-wider font-normal';
  const headStyle = { fontFamily: t.fonts.mono, color: t.muted, borderBottom: `1px solid ${t.hairline}` };
  const cell = { borderBottom: `1px solid ${t.hairline}`, fontFamily: t.fonts.body };
  const timeStyle = { fontFamily: t.fonts.mono, color: t.muted };
  return (
    <>
      {/* Phones: one stacked entry per change, never a sideways-scrolling table. */}
      <div className="md:hidden">
        {entries.map((entry) => {
          const details = portalDetails(entry);
          return (
            <article
              key={`${entry._id || entry.seq}-mobile`}
              className="py-3 border-b"
              style={{ borderColor: t.hairline }}
            >
              <p className="text-base font-medium" style={{ color: t.heading, fontFamily: t.fonts.body }}>
                {portalEventLabel(entry)}
              </p>
              <dl className="mt-1">
                <dt className="sr-only">Actor</dt>
                <dd className="inline text-base break-words" style={{ color: t.body, fontFamily: t.fonts.body }}>
                  {portalActor(entry)}
                </dd>
                <dt className="sr-only">Time</dt>
                <dd className="block mt-0.5 text-[13px] whitespace-nowrap" style={timeStyle}>{formatTime(entry)}</dd>
                {details ? (
                  <>
                    <dt className="sr-only">Details</dt>
                    <dd className="mt-1 text-base break-words [overflow-wrap:anywhere]" style={{ color: t.muted, fontFamily: t.fonts.body }}>
                      {details}
                    </dd>
                  </>
                ) : null}
              </dl>
            </article>
          );
        })}
      </div>
      <div className="hidden md:block">
        <table className="w-full border-collapse">
          <thead>
            <tr>
              <th scope="col" className={`text-left ${headClass}`} style={headStyle}>Event</th>
              <th scope="col" className={`text-left ${headClass}`} style={headStyle}>Details</th>
              <th scope="col" className={`text-left ${headClass}`} style={headStyle}>Actor</th>
              <th scope="col" className={`text-right ${headClass}`} style={headStyle}>Time</th>
            </tr>
          </thead>
          <tbody>
            {entries.map((entry) => {
              return (
                <tr key={entry._id || `${entry.seq}`}>
                  <th
                    scope="row"
                    className="text-left align-top py-2 pr-3 text-base font-normal"
                    style={{ ...cell, color: t.heading }}
                  >
                    {portalEventLabel(entry)}
                  </th>
                  <td className="align-top py-2 pr-3 text-base [overflow-wrap:anywhere]" style={{ ...cell, color: t.muted }}>
                    {portalDetails(entry)}
                  </td>
                  <td className="align-top py-2 pr-3 text-base [overflow-wrap:anywhere]" style={{ ...cell, color: t.body }}>
                    {portalActor(entry)}
                  </td>
                  <td className="align-top py-2 text-[13px] text-right whitespace-nowrap" style={{ ...cell, ...timeStyle }}>
                    {formatTime(entry)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
}

// ---- console: raw rows --------------------------------------------------------

function AuditTable({ entries }) {
  const t = useTokens();
  const headClass = 'py-2 pr-3 text-[11px] uppercase tracking-wider font-normal';
  const headStyle = { fontFamily: t.fonts.mono, color: t.muted, borderBottom: `1px solid ${t.hairline}` };
  return (
    <ScrollTable label="Audit log">
      <table className="w-full min-w-[720px] border-collapse">
        <thead>
          <tr>
            <th scope="col" className={`text-left ${headClass}`} style={headStyle}>Entry</th>
            <th scope="col" className={`text-left ${headClass}`} style={headStyle}>Details</th>
            <th scope="col" className={`text-left ${headClass}`} style={headStyle}>Actor</th>
            <th scope="col" className={`text-right ${headClass}`} style={headStyle}>Time</th>
          </tr>
        </thead>
        <tbody>
          {entries.map((entry) => {
            return <AuditRow key={entry._id || `${entry.seq}`} entry={entry} />;
          })}
        </tbody>
      </table>
    </ScrollTable>
  );
}

function describe(entry) {
  const parts = [];
  const target = entry.target || {};
  if (target.email) { parts.push(target.email); }
  if (target.userId) { parts.push(`user ${target.userId}`); }
  const meta = entry.metadata || {};
  Object.keys(meta).slice(0, 4).forEach((key) => {
    const value = meta[key];
    if (value === null || value === undefined || typeof value === 'object') { return; }
    parts.push(`${key}=${value}`);
  });
  return parts.join(' · ');
}

function AuditRow({ entry }) {
  const t = useTokens();
  const detail = describe(entry);
  const ts = formatTime(entry);
  const actorName = entry.actorName?.trim();
  const actorEmail = entry.actorEmail?.trim();
  const actorId = entry.actor && typeof entry.actor === 'object' ? entry.actor._id : entry.actor;
  const actorPerson = [actorName, actorEmail && actorEmail !== actorName ? actorEmail : null]
    .filter(Boolean)
    .join(' · ')
    || (actorId ? `User ${actorId}` : 'Unknown person');
  const actorLabel = `${actorPerson}${entry.actorDeleted ? ' (deleted user)' : ''}`;
  const cell = { borderBottom: `1px solid ${t.hairline}` };
  return (
    <tr>
      <th
        scope="row"
        className="text-left align-top py-2 pr-3 text-[13px] font-normal"
        style={{ fontFamily: t.fonts.mono, color: t.heading, ...cell }}
      >
        {entry.seq !== undefined ? `#${entry.seq} · ` : ''}{entry.action}
      </th>
      <td className="align-top py-2 pr-3 text-[12px]" style={{ fontFamily: t.fonts.mono, color: t.muted, ...cell }}>
        {detail || 'N/A'}
      </td>
      <td className="align-top py-2 pr-3 text-[12px] whitespace-nowrap" style={{ fontFamily: t.fonts.mono, color: t.muted, ...cell }}>
        <div style={{ color: t.heading }}>{entry.actorType || 'UNKNOWN_ROLE'}</div>
        <div>{actorLabel}</div>
      </td>
      <td className="align-top py-2 text-[12px] text-right whitespace-nowrap" style={{ fontFamily: t.fonts.mono, color: t.muted, ...cell }}>
        {ts}
      </td>
    </tr>
  );
}

function MutedRow({ children }) {
  const t = useTokens();
  return (
    <p className="py-2 text-base" style={{ color: t.muted, fontFamily: t.fonts.body }}>
      {children}
    </p>
  );
}
